/* Bubble theme — vanilla JS, no dependencies. */
(() => {
  'use strict';

  const theme = window.theme || { routes: {}, settings: {}, strings: {} };
  const qs = (sel, root = document) => root.querySelector(sel);
  const qsa = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Money ---------- */
  // "$1,234.56" / "Rp 1.234,56" / "RM1,234.56" → a {{amount…}} format for the shopper's currency.
  const presentmentFormat = (() => {
    let probe = theme.moneyProbe;
    if (!probe) return null;
    if (theme.currency && probe.charAt(0) !== '$') probe = probe.replace(` ${theme.currency}`, '');
    if (probe.includes('1,234.56')) return probe.replace('1,234.56', '{{amount}}');
    if (probe.includes('1.234,56')) return probe.replace('1.234,56', '{{amount_with_comma_separator}}');
    if (probe.includes("1'234.56")) return probe.replace("1'234.56", '{{amount_with_apostrophe_separator}}');
    if (probe.includes('1,235')) return probe.replace('1,235', '{{amount_no_decimals}}');
    if (probe.includes('1.235')) return probe.replace('1.235', '{{amount_no_decimals_with_comma_separator}}');
    return null;
  })();

  function formatMoney(cents, format = presentmentFormat || theme.moneyFormat || '${{amount}}') {
    const value = Number(cents) / 100;
    const fmt = (n, decimals, thousands, decimal) => {
      const fixed = n.toFixed(decimals);
      const [int, frac] = fixed.split('.');
      const withSep = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return frac ? withSep + decimal + frac : withSep;
    };
    const formatted = format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      let out;
      switch (key) {
        case 'amount_no_decimals': out = fmt(value, 0, ',', '.'); break;
        case 'amount_with_comma_separator': out = fmt(value, 2, '.', ','); break;
        case 'amount_no_decimals_with_comma_separator': out = fmt(value, 0, '.', ','); break;
        case 'amount_with_apostrophe_separator': out = fmt(value, 2, "'", '.'); break;
        default: out = fmt(value, 2, ',', '.');
      }
      return out.replace(/[.,]00$/, '');
    });
    const prefix = theme.settings && theme.settings.dollarPrefix;
    return prefix && formatted.charAt(0) === '$' ? `${prefix}${formatted}` : formatted;
  }

  /* ---------- Focus trap ---------- */
  const focusableSel = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';
  function trapFocus(container, event) {
    if (event.key !== 'Tab') return;
    const items = qsa(focusableSel, container).filter((el) => el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && (active === first || active === container || !container.contains(active))) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }

  /* ---------- Header: mega menu, mobile nav, search ---------- */
  // Element listeners are bound per header instance (re-run on shopify:section:load);
  // document listeners are bound once and act on whichever header is current.
  let headerApi = null;
  function initHeader() {
    const header = qs('[data-header]');
    if (!header || header.dataset.ready) return;
    header.dataset.ready = 'true';
    const items = qsa('[data-nav-item]', header);
    let closeTimer;
    let openedBy = null;

    const closeAll = (except) => {
      items.forEach((item) => {
        if (item === except) return;
        const mega = qs('[data-mega]', item);
        const toggle = qs('[data-mega-toggle]', item);
        if (mega) mega.hidden = true;
        if (toggle) toggle.setAttribute('aria-expanded', 'false');
        item.classList.remove('is-open');
      });
    };
    const open = (item, source = null) => {
      openedBy = source;
      const mega = qs('[data-mega]', item);
      if (!mega) { closeAll(); return; }
      closeAll(item);
      mega.hidden = false;
      item.classList.add('is-open');
      const toggle = qs('[data-mega-toggle]', item);
      if (toggle) toggle.setAttribute('aria-expanded', 'true');
    };

    items.forEach((item) => {
      item.addEventListener('mouseenter', () => { clearTimeout(closeTimer); open(item); });
      const toggle = qs('[data-mega-toggle]', item);
      if (toggle) {
        toggle.addEventListener('click', () => {
          const expanded = toggle.getAttribute('aria-expanded') === 'true';
          if (expanded) closeAll(); else { open(item, toggle); const first = qs('.mega__link', item); if (first) first.focus(); }
        });
      }
      item.addEventListener('focusout', (e) => { if (!item.contains(e.relatedTarget)) { const m = qs('[data-mega]', item); if (m && !m.hidden) closeAll(); } });
    });
    header.addEventListener('mouseleave', () => { closeTimer = setTimeout(() => closeAll(), 120); });

    const mobileToggle = qs('[data-mobile-toggle]', header);
    const mobileNav = qs('[data-mobile-nav]', header);
    const searchToggle = qs('[data-search-toggle]', header);
    const searchPanel = qs('[data-header-search]', header);
    const searchInput = qs('[data-predictive-input]', header);

    const setMobile = (state) => {
      if (!mobileNav || !mobileToggle) return;
      mobileNav.hidden = !state;
      mobileToggle.setAttribute('aria-expanded', String(state));
    };
    const setSearch = (state) => {
      if (!searchPanel || !searchToggle) return;
      searchPanel.hidden = !state;
      searchToggle.setAttribute('aria-expanded', String(state));
      if (state && searchInput) setTimeout(() => searchInput.focus(), 10);
    };

    if (mobileToggle) mobileToggle.addEventListener('click', () => { const next = mobileNav.hidden; setSearch(false); closeAll(); setMobile(next); });
    if (searchToggle) searchToggle.addEventListener('click', (e) => { e.preventDefault(); const next = searchPanel.hidden; setMobile(false); closeAll(); setSearch(next); if (!next) searchToggle.focus(); });
    qsa('[data-search-close]', header).forEach((btn) => btn.addEventListener('click', () => { setSearch(false); if (searchToggle) searchToggle.focus(); }));

    headerApi = {
      header,
      escape() {
        const openItem = items.find((i) => i.classList.contains('is-open'));
        if (openItem) { const back = openedBy || qs('.nav__link', openItem); closeAll(); if (back) back.focus(); return true; }
        if (searchPanel && !searchPanel.hidden) { setSearch(false); if (searchToggle) searchToggle.focus(); return true; }
        if (mobileNav && !mobileNav.hidden) { setMobile(false); mobileToggle.focus(); return true; }
        return false;
      },
      outside(target) { if (!header.contains(target)) closeAll(); },
      desktop() { setMobile(false); }
    };
  }
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && headerApi) headerApi.escape(); });
  document.addEventListener('click', (e) => { if (headerApi) headerApi.outside(e.target); });
  window.matchMedia('(min-width: 1080px)').addEventListener('change', (mq) => { if (mq.matches && headerApi) headerApi.desktop(); });
  document.addEventListener('shopify:section:load', () => initHeader());

  /* ---------- Predictive search ---------- */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = qs('[data-predictive-input]', this);
      this.results = qs('[data-predictive-results]', this);
      if (!this.input || !this.results || !theme.routes.predictiveSearch) return;
      this.abort = null;
      this.cache = new Map();
      this.active = -1;
      let timer;
      this.input.addEventListener('input', () => {
        clearTimeout(timer);
        timer = setTimeout(() => this.search(this.input.value.trim()), 220);
      });
      this.input.addEventListener('keydown', (e) => this.onKey(e));
      this.input.addEventListener('focus', () => { if (this.results.innerHTML.trim() && this.input.value.trim()) this.toggle(true); });
      document.addEventListener('click', (e) => { if (!this.contains(e.target)) this.toggle(false); });
    }
    toggle(state) {
      this.results.hidden = !state;
      this.input.setAttribute('aria-expanded', String(state));
      if (!state) { this.active = -1; this.input.removeAttribute('aria-activedescendant'); }
    }
    async search(term) {
      if (this.abort) { this.abort.abort(); this.abort = null; }
      if (!term) { this.toggle(false); this.results.innerHTML = ''; return; }
      if (this.cache.has(term)) { this.render(this.cache.get(term)); return; }
      this.abort = new AbortController();
      const url = `${theme.routes.predictiveSearch}?q=${encodeURIComponent(term)}&resources[type]=product,collection,article&resources[limit]=10&resources[options][fields]=title,product_type,variants.title&resources[options][unavailable_products]=last&section_id=predictive-search`;
      try {
        const res = await fetch(url, { signal: this.abort.signal });
        if (!res.ok) throw new Error(res.status);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const panel = doc.querySelector('[data-predictive-panel]');
        const markup = panel ? panel.outerHTML : '';
        this.cache.set(term, markup);
        if (this.input.value.trim() === term) this.render(markup);
      } catch (err) {
        if (err.name !== 'AbortError') this.toggle(false);
      }
    }
    render(markup) {
      this.results.innerHTML = markup;
      this.active = -1;
      this.toggle(Boolean(markup));
    }
    onKey(e) {
      const options = qsa('[role="option"]', this.results);
      if (e.key === 'Escape') { if (!this.results.hidden) { e.stopPropagation(); this.toggle(false); } return; }
      if (!options.length || this.results.hidden) return;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        this.active = (this.active + (e.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
        options.forEach((o, i) => o.setAttribute('aria-selected', String(i === this.active)));
        this.input.setAttribute('aria-activedescendant', options[this.active].id);
        options[this.active].scrollIntoView({ block: 'nearest' });
      } else if (e.key === 'Enter' && this.active > -1) {
        e.preventDefault();
        const link = qs('a', options[this.active]);
        if (link) window.location.href = link.href;
      }
    }
  }
  if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);

  /* ---------- Hero slideshow ---------- */
  class HeroSlideshow extends HTMLElement {
    connectedCallback() {
      this.slides = qsa('[data-slide]', this);
      this.dots = qsa('[data-dot]', this);
      this.pauseBtn = qs('[data-pause]', this);
      this.index = 0;
      this.interval = Number(this.dataset.interval) || 6000;
      this.userPaused = false;
      this.hydrateLater();
      if (this.slides.length < 2) return;
      if (reducedMotion && this.pauseBtn) this.pauseBtn.hidden = true;
      this.dots.forEach((dot) => dot.addEventListener('click', () => { this.go(Number(dot.dataset.dot)); this.pause(true); }));
      if (this.pauseBtn) this.pauseBtn.addEventListener('click', () => (this.userPaused ? this.play(true) : this.pause(true)));
      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.start());
      this.addEventListener('focusin', () => this.stop());
      this.addEventListener('focusout', (e) => { if (!this.contains(e.relatedTarget)) this.start(); });
      document.addEventListener('visibilitychange', () => (document.hidden ? this.stop() : this.start()));
      if (this.dataset.autoplay === 'true' && !reducedMotion) this.start();
      else this.pause(false);
      document.addEventListener('shopify:block:select', (e) => {
        const i = this.slides.findIndex((s) => s.contains(e.target));
        if (i > -1) { this.go(i); this.stop(); }
      });
    }
    disconnectedCallback() { this.stop(); }
    hydrate(slide) {
      qsa('template[data-deferred-media]', slide).forEach((tpl) => tpl.replaceWith(tpl.content.cloneNode(true)));
    }
    hydrateLater() {
      const run = () => this.slides.forEach((s) => this.hydrate(s));
      if (document.readyState === 'complete') setTimeout(run, 1500);
      else window.addEventListener('load', () => setTimeout(run, 1500), { once: true });
    }
    go(i) {
      this.index = (i + this.slides.length) % this.slides.length;
      this.hydrate(this.slides[this.index]);
      this.slides.forEach((slide, n) => {
        const active = n === this.index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        qsa('a, button', slide).forEach((el) => (active ? el.removeAttribute('tabindex') : el.setAttribute('tabindex', '-1')));
      });
      this.dots.forEach((dot, n) => {
        dot.classList.toggle('is-active', n === this.index);
        if (n === this.index) dot.setAttribute('aria-current', 'true'); else dot.removeAttribute('aria-current');
      });
    }
    start() {
      if (this.userPaused || this.dataset.autoplay !== 'true' || reducedMotion) return;
      this.stop();
      this.timer = setInterval(() => this.go(this.index + 1), this.interval);
    }
    stop() { clearInterval(this.timer); this.timer = null; }
    pause(byUser) {
      if (byUser) this.userPaused = true;
      this.stop();
      this.classList.add('is-paused');
      if (this.pauseBtn) this.pauseBtn.setAttribute('aria-label', this.pauseBtn.dataset.labelPlay);
    }
    play(byUser) {
      if (byUser) this.userPaused = false;
      this.classList.remove('is-paused');
      if (this.pauseBtn) this.pauseBtn.setAttribute('aria-label', this.pauseBtn.dataset.labelPause);
      this.start();
    }
  }
  if (!customElements.get('hero-slideshow')) customElements.define('hero-slideshow', HeroSlideshow);

  /* ---------- Cart ---------- */
  const Cart = {
    queue: Promise.resolve(),
    pending: 0,
    enqueue(task) {
      this.pending += 1;
      document.documentElement.classList.add('cart-busy');
      const run = this.queue.then(task, task);
      this.queue = run.catch(() => {}).finally(() => {
        this.pending -= 1;
        if (!this.pending) document.documentElement.classList.remove('cart-busy');
      });
      return run;
    },
    sectionIds() {
      const ids = [];
      if (qs('[data-cart-drawer]')) ids.push('cart-drawer');
      const page = qs('[data-cart-page]');
      if (page && page.closest('.shopify-section')) ids.push(page.closest('.shopify-section').id.replace('shopify-section-', ''));
      return ids;
    },
    add(formData) { return this.enqueue(() => this.doAdd(formData)); },
    change(key, quantity) { return this.enqueue(() => this.doChange(key, quantity)); },
    async doAdd(formData) {
      const ids = this.sectionIds();
      if (ids.length) {
        formData.append('sections', ids.join(','));
        formData.append('sections_url', window.location.pathname);
      }
      const res = await fetch(`${theme.routes.cartAdd}.js`, { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: formData });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || theme.strings.cartError);
      if (data.sections) this.renderSections(data.sections);
      await this.refreshCount();
      return data;
    },
    async doChange(key, quantity) {
      const ids = this.sectionIds();
      const res = await fetch(`${theme.routes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity, sections: ids, sections_url: window.location.pathname })
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || theme.strings.cartError);
      if (data.sections) this.renderSections(data.sections);
      this.setCount(data.item_count);
      return data;
    },
    renderSections(sections) {
      Object.entries(sections).forEach(([id, html]) => {
        if (!html) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        if (id === 'cart-drawer') {
          const fresh = qs('[data-cart-drawer-content]', doc);
          const current = qs('[data-cart-drawer-content]');
          if (fresh && current) current.innerHTML = fresh.innerHTML;
        } else {
          const fresh = qs('[data-cart-page-content]', doc);
          const current = qs('[data-cart-page-content]');
          if (fresh && current) current.innerHTML = fresh.innerHTML;
        }
      });
    },
    async refreshCount() {
      try {
        const res = await fetch(`${theme.routes.cart}.js`, { headers: { Accept: 'application/json' } });
        const cart = await res.json();
        this.setCount(cart.item_count);
      } catch (e) { /* non-fatal */ }
    },
    setCount(count) {
      qsa('[data-cart-count]').forEach((el) => { el.textContent = count; el.classList.toggle('is-empty', !count); });
      qsa('[data-cart-count-label]').forEach((el) => { el.textContent = el.textContent.replace(/\d+/, count); });
    }
  };
  window.BubbleCart = Cart;

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = qs('.drawer__panel', this);
      this.addEventListener('click', (e) => { if (e.target.closest('[data-close-cart]')) this.close(); });
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); trapFocus(this.panel, e); });
      document.addEventListener('shopify:section:select', (e) => { if (e.detail && e.detail.sectionId === 'cart-drawer') this.open(); });
    }
    open() {
      this.lastFocus = document.activeElement;
      this.hidden = false;
      document.documentElement.style.overflow = 'hidden';
      requestAnimationFrame(() => { const close = qs('[data-close-cart].icon-btn', this) || this.panel; close.focus(); });
    }
    focusStart() { const close = qs('[data-close-cart].icon-btn', this) || this.panel; close.focus(); }
    close() {
      if (this.hidden) return;
      this.hidden = true;
      document.documentElement.style.overflow = '';
      if (this.lastFocus && document.body.contains(this.lastFocus)) this.lastFocus.focus();
    }
  }
  if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);
  const drawer = () => (theme.settings.cartType === 'drawer' ? qs('cart-drawer') : null);

  // Open drawer from cart icon / toast
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-open-cart]');
    if (!trigger) return;
    const d = drawer();
    if (!d) return;
    e.preventDefault();
    hideToast();
    d.open();
  });

  // Quantity changes in drawer / cart page (links work without JS; we enhance them)
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-cart-qty]');
    if (!btn) return;
    e.preventDefault();
    if (Cart.pending) return;
    const surface = btn.closest('[data-cart-drawer-content], [data-cart-page-content]');
    const surfaceSel = surface && surface.matches('[data-cart-drawer-content]') ? '[data-cart-drawer-content]' : '[data-cart-page-content]';
    if (surface) surface.classList.add('is-loading');
    const key = btn.dataset.key;
    const action = btn.dataset.action;
    const requested = Number(btn.dataset.qty);
    try {
      const cart = await Cart.change(key, requested);
      const line = cart.items && cart.items.find((i) => i.key === key);
      if (line && requested > line.quantity) showToast(theme.strings.maxQty.replace('[quantity]', line.quantity), null, true);
      const fresh = qs(surfaceSel);
      const restore = fresh && line && qs(`[data-cart-qty][data-key="${CSS.escape(key)}"][data-action="${action}"]`, fresh);
      if (restore) restore.focus();
      else { const d = drawer(); if (d && !d.hidden && surfaceSel === '[data-cart-drawer-content]') d.focusStart(); }
    } catch (err) {
      showToast(err.message, null, true);
    } finally {
      const current = qs(surfaceSel);
      if (current) current.classList.remove('is-loading');
    }
  });

  // Cart note (cart page)
  document.addEventListener('change', (e) => {
    const note = e.target.closest('[data-cart-note]');
    if (!note) return;
    fetch(`${theme.routes.cartUpdate}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note: note.value }) });
  });

  /* ---------- Toast ---------- */
  let toastTimer;
  function showToast(text, image, isError = false) {
    const toast = qs('[data-toast]');
    if (!toast) return;
    qs('[data-toast-text]', toast).textContent = text;
    const img = qs('[data-toast-img]', toast);
    if (image) { img.src = image; img.hidden = false; } else { img.hidden = true; }
    const btn = qs('[data-open-cart]', toast);
    if (btn) btn.hidden = isError;
    toast.classList.toggle('toast--error', isError);
    toast.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(hideToast, isError ? 4000 : 2400);
  }
  function hideToast() { const t = qs('[data-toast]'); if (t) t.hidden = true; }
  document.addEventListener('DOMContentLoaded', () => {
    const toast = qs('[data-toast]');
    if (!toast) return;
    const hold = () => clearTimeout(toastTimer);
    const release = () => { clearTimeout(toastTimer); toastTimer = setTimeout(hideToast, 2400); };
    toast.addEventListener('mouseenter', hold);
    toast.addEventListener('focusin', hold);
    toast.addEventListener('mouseleave', release);
    toast.addEventListener('focusout', release);
  });

  /* ---------- Product forms (cards + product page) ---------- */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-product-form]');
    if (!form || !window.fetch) return;
    e.preventDefault();
    const submit = form.querySelector('[type="submit"]');
    const errorEl = form.querySelector('[data-form-error]');
    if (submit) { submit.setAttribute('aria-busy', 'true'); submit.disabled = true; }
    if (errorEl) errorEl.hidden = true;
    try {
      const fd = new FormData(form);
      const item = await Cart.add(fd);
      const image = form.dataset.toastImage || (item && item.image ? `${item.image}${item.image.includes('?') ? '&' : '?'}width=120` : null);
      const d = drawer();
      if (d && theme.settings.afterAdd === 'drawer') d.open();
      else showToast(theme.strings.added, image);
    } catch (err) {
      if (errorEl) { errorEl.textContent = err.message; errorEl.hidden = false; }
      else showToast(err.message, null, true);
    } finally {
      if (submit) { submit.removeAttribute('aria-busy'); if (!submit.hasAttribute('data-sold-out')) submit.disabled = false; }
    }
  });

  /* ---------- Quantity input ---------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = qs('input', this);
      qsa('button', this).forEach((btn) => btn.addEventListener('click', () => {
        const current = Math.max(1, Math.floor(Number(this.input.value)) || 1);
        this.input.value = Math.max(1, btn.name === 'plus' ? current + 1 : current - 1);
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      }));
      this.input.addEventListener('change', () => {
        const n = Math.floor(Number(this.input.value));
        this.input.value = n >= 1 ? n : 1;
        updateAddTotal(this.closest('form'));
      });
    }
  }
  if (!customElements.get('quantity-input')) customElements.define('quantity-input', QuantityInput);

  function updateAddTotal(form) {
    if (!form) return;
    const btn = qs('[data-add-button]', form);
    const total = btn && qs('[data-add-total]', btn);
    const qty = qs('[data-qty]', form);
    if (!btn || !total) return;
    total.textContent = formatMoney(Number(btn.dataset.unitPrice) * (Number(qty && qty.value) || 1));
  }

  /* ---------- Product gallery ---------- */
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.slides = qsa('[data-media-id]', this);
      this.thumbs = qsa('[data-thumb]', this);
      this.thumbs.forEach((t) => t.addEventListener('click', () => this.show(t.dataset.thumb)));
    }
    show(id) {
      if (!this.slides.some((s) => s.dataset.mediaId === String(id))) return;
      this.slides.forEach((s) => {
        const active = s.dataset.mediaId === String(id);
        s.hidden = !active;
        s.classList.toggle('is-active', active);
        if (!active) qsa('video', s).forEach((v) => v.pause());
      });
      this.thumbs.forEach((t) => {
        const active = t.dataset.thumb === String(id);
        t.classList.toggle('is-active', active);
        if (active) t.setAttribute('aria-current', 'true'); else t.removeAttribute('aria-current');
      });
    }
  }
  if (!customElements.get('product-gallery')) customElements.define('product-gallery', ProductGallery);

  /* ---------- Variant picker ---------- */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      const json = qs('[data-variants]', this);
      this.variants = json ? JSON.parse(json.textContent) : [];
      this.section = this.closest('[data-product-section]');
      const fallback = this.section && qs('.variants__fallback', this.section);
      if (fallback) fallback.hidden = true;
      this.addEventListener('change', () => this.onChange());
      this.markAvailability();
    }
    selected() {
      return qsa('fieldset', this).map((fs) => { const c = qs('input:checked', fs); return c ? c.value : null; });
    }
    markAvailability() {
      const sel = this.selected();
      qsa('fieldset', this).forEach((fs, idx) => {
        qsa('input', fs).forEach((input) => {
          const test = sel.slice(); test[idx] = input.value;
          const match = this.variants.find((v) => v.options.every((o, i) => o === test[i]));
          const label = input.nextElementSibling;
          if (label) label.classList.toggle('is-unavailable', !match || !match.available);
        });
      });
    }
    onChange() {
      const sel = this.selected();
      qsa('[data-option-value]', this).forEach((el, i) => { el.textContent = sel[i] || ''; });
      const variant = this.variants.find((v) => v.options.every((o, i) => o === sel[i]));
      this.markAvailability();
      const form = this.section && qs('[data-product-form]', this.section);
      const btn = form && qs('[data-add-button]', form);
      const label = btn && qs('[data-add-label]', btn);
      if (!variant) {
        const skuRow = this.section && qs('[data-sku-row]', this.section);
        if (skuRow) skuRow.hidden = true;
        if (btn) { btn.disabled = true; btn.setAttribute('data-sold-out', ''); btn.innerHTML = `<span data-add-label>${theme.strings.unavailable}</span>`; }
        const priceWrap = this.section && qs('[data-product-price]', this.section);
        if (priceWrap) priceWrap.innerHTML = `<span class="price" data-price>${theme.strings.unavailable}</span>`;
        return;
      }
      const idInput = form && qs('[data-variant-id]', form);
      if (idInput) idInput.value = variant.id;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
      const priceWrap = this.section && qs('[data-product-price]', this.section);
      if (priceWrap) {
        const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
        priceWrap.innerHTML = `<span class="price${onSale ? ' price--sale' : ''}" data-price><span class="price__now">${formatMoney(variant.price)}</span>${onSale ? `<span class="visually-hidden">${theme.strings.regularPrice}</span><s class="price__was">${formatMoney(variant.compare_at_price)}</s>` : ''}</span>`;
      }
      if (btn) {
        btn.dataset.unitPrice = variant.price;
        btn.disabled = !variant.available;
        if (variant.available) btn.removeAttribute('data-sold-out'); else btn.setAttribute('data-sold-out', '');
        btn.innerHTML = variant.available
          ? `<span data-add-label>${theme.strings.addToCart}</span> · <span data-add-total></span>`
          : `<span data-add-label>${theme.strings.soldOut}</span>`;
        updateAddTotal(form);
      }
      const sku = this.section && qs('[data-sku]', this.section);
      if (sku) sku.textContent = variant.sku || '';
      const skuRow = this.section && qs('[data-sku-row]', this.section);
      if (skuRow) skuRow.hidden = !variant.sku;
      const gallery = this.section && qs('product-gallery', this.section);
      if (gallery && variant.featured_media) gallery.show(variant.featured_media.id);
    }
  }
  if (!customElements.get('variant-picker')) customElements.define('variant-picker', VariantPicker);

  /* ---------- Product recommendations ---------- */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url || this.children.length) return;
      const load = () => fetch(this.dataset.url).then((r) => r.text()).then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('product-recommendations');
        if (fresh && fresh.innerHTML.trim()) this.innerHTML = fresh.innerHTML;
      }).catch(() => {});
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries) => { if (entries.some((en) => en.isIntersecting)) { io.disconnect(); load(); } }, { rootMargin: '0px 0px 400px 0px' });
        io.observe(this);
      } else load();
    }
  }
  if (!customElements.get('product-recommendations')) customElements.define('product-recommendations', ProductRecommendations);

  /* ---------- Collection filters ---------- */
  // The drawer is a <details> and the form a plain GET, so filters work without JS.
  // With JS, every change re-renders the section via the Section Rendering API.
  // Only the newest request may touch the page: older responses are aborted and ignored.
  const Facets = {
    controller: null,
    generation: 0,
    timer: null,
    current: window.location.search, // the query the results on screen belong to
    target: null, // the query being fetched, if any
    form: () => qs('[data-filters-form]'),
    query: (params) => { const s = params.toString(); return s ? `?${s}` : ''; },
    searchFrom(form) {
      const params = new URLSearchParams();
      new FormData(form).forEach((value, key) => { if (String(value).trim() !== '') params.append(key, value); });
      return params;
    },
    // The query the shopper most recently asked for, even if it is still loading.
    latest() { return new URLSearchParams((this.target !== null ? this.target : this.current).replace(/^\?/, '')); },
    async render(params, { push = true, full = false } = {}) {
      clearTimeout(this.timer);
      const form = this.form();
      if (!form) return;
      const query = this.query(params);
      const pageUrl = `${form.getAttribute('action')}${query}`;
      if (this.controller) this.controller.abort();
      const controller = new AbortController();
      const generation = ++this.generation;
      this.controller = controller;
      this.target = query;
      const results = qs('[data-results]');
      if (results) { results.classList.add('is-loading'); results.setAttribute('aria-busy', 'true'); }
      try {
        const res = await fetch(`${pageUrl}${query ? '&' : '?'}section_id=${encodeURIComponent(form.dataset.section)}`, { signal: controller.signal });
        if (!res.ok) throw new Error(res.status);
        const html = await res.text();
        if (generation !== this.generation) return;
        this.swap(new DOMParser().parseFromString(html, 'text/html'), full);
        this.current = query;
        if (push) window.history.pushState({ facets: true }, '', pageUrl);
      } catch (err) {
        if (err.name !== 'AbortError' && generation === this.generation) window.location.href = pageUrl;
      } finally {
        if (generation === this.generation) {
          this.target = null;
          this.controller = null;
          const fresh = qs('[data-results]');
          if (fresh) { fresh.classList.remove('is-loading'); fresh.removeAttribute('aria-busy'); }
        }
      }
    },
    cancel() {
      clearTimeout(this.timer);
      if (this.controller) this.controller.abort();
      this.generation += 1;
      this.controller = null;
      this.target = null;
    },
    swap(doc, full) {
      const focused = document.activeElement;
      const focusedId = focused && focused.id;
      const replace = (sel) => { const fresh = qs(sel, doc); const cur = qs(sel); if (fresh && cur) cur.replaceWith(fresh); };
      const text = (sel) => {
        const fresh = qsa(sel, doc);
        qsa(sel).forEach((cur, i) => {
          if (!fresh[i]) return;
          if (cur.textContent !== fresh[i].textContent) cur.textContent = fresh[i].textContent;
          cur.hidden = fresh[i].hidden;
        });
      };
      ['[data-results]', '[data-active-filters]', '[data-sort-form]'].forEach(replace);
      ['[data-results-count]', '[data-filter-count]', '[data-filter-count-sr]', '[data-filters-submit]', '[data-filters-clear]'].forEach(text);
      // The drawer is a modal, so it carries its own live region; on desktop the toolbar count announces instead.
      const filtersEl = qs('facet-filters');
      if (filtersEl && filtersEl.isOpen) text('[data-filters-status]');
      if (full) {
        // Chips / clear all / history: rebuild every facet (price inputs included), keeping open/closed state.
        const body = qs('[data-facets-body]');
        const freshBody = qs('[data-facets-body]', doc);
        if (body && freshBody) {
          const closed = qsa('details[data-facet]:not([open])', body).map((d) => d.dataset.facet);
          body.innerHTML = freshBody.innerHTML;
          closed.forEach((key) => { const d = qs(`details[data-facet="${CSS.escape(key)}"]`, body); if (d) d.open = false; });
        }
      } else {
        // Checkbox changes: refresh counts and disabled states but leave typed prices alone.
        qsa('[data-facet-values]').forEach((cur) => {
          const fresh = qs(`[data-facet-values="${CSS.escape(cur.dataset.facetValues)}"]`, doc);
          if (fresh) cur.innerHTML = fresh.innerHTML;
        });
      }
      // Put focus back on the same control, or somewhere sensible inside the open drawer.
      if (focused && !document.contains(focused)) {
        let next = focusedId && document.getElementById(focusedId);
        if (next && (next.disabled || next.offsetParent === null)) next = null;
        const filters = qs('facet-filters');
        if (!next && filters && filters.isOpen) next = qs('.filters__close', filters);
        if (!next && filters && !filtersDrawerQuery.matches) next = qs('.filters__title', filters);
        if (next) next.focus();
      }
    }
  };

  // Desktop: a static column. Below 990px: a drawer opened from the toolbar's Filter button.
  const filtersDrawerQuery = window.matchMedia('(max-width: 989.98px)');
  class FacetFilters extends HTMLElement {
    connectedCallback() {
      this.panel = qs('.filters__panel', this);
      this.form = qs('[data-filters-form]', this);
      if (!this.form) return;
      // Crossing the breakpoint hides whatever had focus (sidebar controls or the drawer's Close), so move it somewhere visible.
      this.onMedia = () => {
        const hadFocus = this.contains(document.activeElement);
        if (filtersDrawerQuery.matches) {
          const toggle = this.toggles[0];
          if (hadFocus && toggle) toggle.focus();
        } else {
          this.close(false);
          if (hadFocus) qs('.filters__title', this).focus();
        }
      };
      filtersDrawerQuery.addEventListener('change', this.onMedia);
      this.addEventListener('click', (e) => { if (e.target.closest('[data-filters-close]')) { e.preventDefault(); this.close(); } });
      this.addEventListener('keydown', (e) => {
        if (!this.isOpen) return;
        if (e.key === 'Escape') { e.stopPropagation(); this.close(); return; }
        trapFocus(this.panel, e);
      });
      this.form.addEventListener('change', (e) => {
        if (e.target.type === 'number') this.debounce(0);
        else Facets.render(Facets.searchFrom(this.form));
      });
      this.form.addEventListener('input', (e) => { if (e.target.type === 'number') this.debounce(700); });
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        const params = Facets.searchFrom(this.form);
        if (Facets.query(params) !== Facets.current || Facets.target !== null) Facets.render(params);
        else clearTimeout(Facets.timer);
        this.close();
      });
      // Arrived with #Filters-… in the URL (the no-JS way of opening): switch to the JS drawer.
      if (window.location.hash === `#${this.id}`) {
        window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
        if (filtersDrawerQuery.matches) this.open();
      }
    }
    disconnectedCallback() {
      // Theme editor re-renders: don't leave the page scroll-locked or a request mid-flight.
      Facets.cancel();
      filtersDrawerQuery.removeEventListener('change', this.onMedia);
      if (this.isOpen) document.documentElement.style.overflow = '';
    }
    get toggles() { return qsa(`[data-filters-open][aria-controls="${CSS.escape(this.id)}"]`); }
    debounce(ms) {
      clearTimeout(Facets.timer);
      Facets.timer = setTimeout(() => Facets.render(Facets.searchFrom(this.form)), ms);
    }
    open() {
      if (!filtersDrawerQuery.matches || this.isOpen) return;
      this.isOpen = true;
      this.lastFocus = document.activeElement;
      this.classList.add('is-open');
      this.panel.setAttribute('role', 'dialog');
      this.panel.setAttribute('aria-modal', 'true');
      this.toggles.forEach((t) => t.setAttribute('aria-expanded', 'true'));
      document.documentElement.style.overflow = 'hidden';
      const close = qs('.filters__close', this) || this.panel;
      requestAnimationFrame(() => close.focus());
    }
    close(restoreFocus = true) {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.classList.remove('is-open');
      this.panel.removeAttribute('role');
      this.panel.removeAttribute('aria-modal');
      this.toggles.forEach((t) => t.setAttribute('aria-expanded', 'false'));
      document.documentElement.style.overflow = '';
      if (!restoreFocus) return;
      const back = this.toggles[0] || this.lastFocus;
      if (back && document.body.contains(back)) back.focus();
    }
  }
  if (!customElements.get('facet-filters')) customElements.define('facet-filters', FacetFilters);

  document.addEventListener('click', (e) => {
    const toggle = e.target.closest('[data-filters-open]');
    if (!toggle) return;
    const filters = document.getElementById(toggle.getAttribute('aria-controls'));
    if (!filters || typeof filters.open !== 'function') return;
    e.preventDefault();
    filters.open();
  });

  // Chips, "Clear all" and the empty-state reset link
  document.addEventListener('click', (e) => {
    const link = e.target.closest('a[data-filter-link]');
    if (!link || !Facets.form() || e.metaKey || e.ctrlKey || e.shiftKey) return;
    e.preventDefault();
    let params;
    if (link.dataset.removeParam) {
      // Remove from the newest query, not the one this chip was rendered with, so quick clicks add up.
      const names = link.dataset.removeParam.split(',');
      const value = link.dataset.removeValue;
      params = new URLSearchParams();
      Facets.latest().forEach((v, k) => { if (!(names.includes(k) && (value === undefined || v === value))) params.append(k, v); });
    } else {
      params = new URL(link.href, window.location.origin).searchParams;
    }
    const chips = qsa('[data-active-filters] a[data-filter-link]');
    const chipIndex = chips.indexOf(link);
    const fromOutside = !link.closest('facet-filters');
    Facets.render(params, { full: true }).then(() => {
      // Chips and the empty-state link are replaced by the render: focus the chip that took this one's place,
      // else the Filter button (mobile) or the filter column's heading (desktop).
      if (!fromOutside || document.activeElement !== document.body) return;
      const fresh = qsa('[data-active-filters] a[data-filter-link]');
      const toggle = qs('[data-filters-open]');
      const target = (chipIndex > -1 && fresh[Math.min(chipIndex, fresh.length - 1)])
        || (toggle && toggle.offsetParent !== null ? toggle : qs('.filters__title'));
      if (target) target.focus();
    });
  });
  window.addEventListener('popstate', () => {
    if (!Facets.form()) return;
    if (window.location.search === Facets.current && Facets.target === null) { clearTimeout(Facets.timer); return; }
    Facets.render(new URLSearchParams(window.location.search), { push: false, full: true });
  });

  /* ---------- Show more (collection grid when sold-out products are listed last) ---------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-show-more]');
    if (!btn) return;
    const results = btn.closest('[data-results]') || document;
    const hidden = qsa('[data-product-grid] > li[data-later][hidden]', results);
    const batch = hidden.slice(0, Number(btn.dataset.step) || 24);
    batch.forEach((li) => { li.hidden = false; });
    const total = qsa('[data-product-grid] > li', results).length;
    const remaining = hidden.length - batch.length;
    const status = qs('[data-show-more-status]', results);
    if (status) status.textContent = status.dataset.template.replace('[shown]', total - remaining);
    if (!remaining) btn.hidden = true;
    const first = batch[0] && qs('.card__link', batch[0]);
    if (first) first.focus({ preventScroll: true });
  });

  /* ---------- Card hover images ---------- */
  if (window.matchMedia('(hover: hover)').matches) {
    document.addEventListener('pointerover', (e) => {
      const media = e.target.closest && e.target.closest('.card__media');
      if (!media) return;
      const img = qs('img[data-hover-src]', media);
      if (!img) return;
      img.srcset = img.dataset.hoverSrcset;
      img.src = img.dataset.hoverSrc;
      img.removeAttribute('data-hover-src');
      img.hidden = false;
    });
  }

  /* ---------- Misc ---------- */
  document.addEventListener('change', (e) => {
    const el = e.target.closest('[data-autosubmit]');
    if (!el || !el.form) return;
    if (el.form.matches('[data-sort-form]') && Facets.form()) {
      Facets.render(new URLSearchParams(new FormData(el.form)), { full: true });
      return;
    }
    el.form.submit();
  });

  // Anchors styled as buttons respond to Space like real buttons
  document.addEventListener('keydown', (e) => {
    if (e.key !== ' ' || !e.target.matches('a[role="button"]')) return;
    e.preventDefault();
    e.target.click();
  });

  document.addEventListener('submit', (e) => {
    const form = e.target.closest('[data-confirm]');
    if (form && !window.confirm(form.dataset.confirm)) e.preventDefault();
  });

  function initLogin() {
    const recover = qs('[data-recover]');
    const login = qs('[data-login]');
    if (!recover || !login) return;
    const show = (which) => { recover.hidden = which !== 'recover'; login.hidden = which === 'recover'; };
    if (window.location.hash === '#recover' || qs('.form-success', recover)) show('recover');
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-show-recover]')) { e.preventDefault(); show('recover'); qs('input[type="email"]', recover).focus(); }
      if (e.target.closest('[data-show-login]')) { e.preventDefault(); show('login'); qs('input[type="email"]', login).focus(); }
    });
  }

  function initAddressCountry() {
    qsa('select[data-default]').forEach((sel) => { if (sel.dataset.default) sel.value = sel.dataset.default; });
  }

  document.addEventListener('DOMContentLoaded', () => {
    initHeader();
    initLogin();
    initAddressCountry();
    qsa('[data-product-form]').forEach(updateAddTotal);
  });
})();
